import { RadioGroupButtons, SimpleAlert } from '@cimplico/components';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import XeroForm from './XeroForm';
import VerticalRadioInput from '../../components/radio-input/VerticalRadioInput';
import ManualBulkEntityForm from './ManualBulkEntityForm';
import ManualSingleEntityForm from './ManualSingleEntityForm';
import CsvFileRowError from '../../components/error-alerts/CsvFileRowError';
import plural from '../../utilities/plural';
import UpgradePrompt from '../../components/upgrade-prompt/UpgradePrompt';
import { SubscriptionTier } from '../../types/firm';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';

const tabs = ['manual', 'xero'];

function NewEntityForm(): JSX.Element {
    const [params, setParams] = useSearchParams();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [provider, setProvider] = useState(tabs.includes(params.get('tab') || '') ? (params.get('tab') as string) : 'manual');
    const [isBulk, setIsBulk] = useState(false);
    const [fileError, setFileError] = useState<string | undefined>(undefined);
    const [csvRowError, setCsvRowError] = useState<{ [rowNumber: string]: string[] }>({});
    const [createEntityError, setCreateEntityError] = useState<{ [name: string]: string[] }>({});
    const [promptUpgrade, setPromptUpgrade] = useState(false);
    const featureAccess = { canBulkImportEntities: useHasFeatureAccess('entity-bulk-import:write') };

    function handleSourceChange(e: React.ChangeEvent<HTMLInputElement>): void {
        setProvider(e.target.value);
        clearError();

        const searchParams = new URLSearchParams(params.toString());
        searchParams.set('tab', e.target.value);
        setParams(searchParams.toString());
    }

    function clearError(): void {
        setFileError(undefined);
        setCsvRowError({});
        setCreateEntityError({});
    }

    return (
        <div className={`${provider === 'xero' ? 'max-w-[850px]' : 'max-w-[450px]'} mx-auto mt-8`}>
            {fileError && (
                <div className="mb-4">
                    <SimpleAlert type="error" title={fileError} />
                </div>
            )}

            {promptUpgrade && <UpgradePrompt onClose={(): void => setPromptUpgrade(false)} targetTier={SubscriptionTier.ESSENTIAL} />}

            {/* PRINT ERROR MESSAGES FOR EVERY ROWS */}
            {Object.keys(csvRowError).length > 0 && <CsvFileRowError errorMessages={csvRowError} />}

            {/* PRINT ERROR MESSAGES FOR CREATE ERROR */}
            {Object.keys(createEntityError).length > 0 && (
                <CsvFileRowError
                    errorMessages={createEntityError}
                    title={`Failed to create ${Object.keys(createEntityError).length} ${plural(
                        Object.keys(createEntityError).length,
                        'entity',
                        'entities'
                    )}`}
                    useRowAsIdentifier={false}
                />
            )}

            <div className="mb-8">
                <RadioGroupButtons
                    groupName="importType"
                    onChange={handleSourceChange}
                    options={[
                        {
                            label: 'Manual',
                            value: 'manual',
                            disabled: isSubmitting,
                        },
                        {
                            label: 'Xero',
                            value: 'xero',

                            disabled: isSubmitting,
                        },
                    ]}
                    title="Source"
                    value={provider}
                />
            </div>
            {provider === 'manual' && (
                <div className="flex flex-col gap-8">
                    <VerticalRadioInput
                        title="Import bulk"
                        description="Would you like to bulk import your entities?"
                        optionOneLabel="No"
                        optionTwoLabel="Yes"
                        isDisabledOptionOne={isSubmitting}
                        isDisabledOptionTwo={isSubmitting}
                        value={isBulk ? 'yes' : 'no'}
                        onValueChange={(val): void => {
                            if (!featureAccess.canBulkImportEntities && val === 'yes') {
                                setPromptUpgrade(true);
                                setIsBulk(false);
                                return;
                            }
                            setIsBulk(val === 'yes');
                            clearError();
                        }}
                    />
                    {isBulk ? (
                        <ManualBulkEntityForm
                            onLoading={(): void => setIsSubmitting(true)}
                            onSubmit={(): void => setIsSubmitting(false)}
                            onFileError={setFileError}
                            onCreateEntityError={setCreateEntityError}
                            onCsvRowError={setCsvRowError}
                        />
                    ) : (
                        <ManualSingleEntityForm
                            onLoading={(): void => setIsSubmitting(true)}
                            onSubmit={(): void => setIsSubmitting(false)}
                        />
                    )}
                </div>
            )}
            {provider === 'xero' && <XeroForm />}
        </div>
    );
}

export default NewEntityForm;
